(function () {
  if (customElements.get('cart-popup')) return;

  const esc = window._erj_.esc;

  class CartPopup extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._bagMode = true;
      this._wishlistLoaded = false;
      this._wishlistEverFetched = false;
      this._hideTimer = null;
      this._cartEl = null;
      this._tabs = null;
      this._wishlistCfg = {};
      this._onWindowEvent = this._onWindowEvent.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._onWishlist = this._onWishlist.bind(this);
      this._onWishlistClick = this._onWishlistClick.bind(this);
    }

    connectedCallback() {
      this._cartEl = this;
      this._cartEl.dataset.mode = 'bag';
      this._wishlistCfg = this._readWishlistConfig();
      CartPopup._WINDOW_EVENTS.forEach(e => window.addEventListener(e, this._onWindowEvent));
      document.addEventListener('click', this._onDocClick);

      // Native wishlist tab: render straight from window.wishlistProvider and
      // re-render on provider changes (no Vue).
      window.addEventListener('wishlist:updated', this._onWishlist);
      window.addEventListener('wishlist:ready', this._onWishlist);
      this.addEventListener('click', this._onWishlistClick);
      this._renderWishlist();

      this._tabs = this.querySelector('swipe-tabs');
      this._tabs?.configure({
        root: this,
        enabled: () => this._open,
        isFirst: () => this._bagMode,
        prepare: (isBag) => this._prepareModeData(isBag),
        select: (isBag) => this.setMode(isBag),
        commit: (isBag) => {
          this._bagMode = isBag;
          this.dataset.mode = isBag ? 'bag' : 'wishlist';
        },
        updateTabs: () => this._updateTabClasses(),
      });
    }

    disconnectedCallback() {
      clearTimeout(this._hideTimer);
      this._tabs?.reset();
      CartPopup._WINDOW_EVENTS.forEach(e => window.removeEventListener(e, this._onWindowEvent));
      document.removeEventListener('click', this._onDocClick);
      window.removeEventListener('wishlist:updated', this._onWishlist);
      window.removeEventListener('wishlist:ready', this._onWishlist);
      this.removeEventListener('click', this._onWishlistClick);
    }

    _readWishlistConfig() {
      const raw = this.getAttribute('data-wishlist-config');
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch (e) {
        return {};
      }
    }

    _onWindowEvent(e) {
      if (e.type === 'enroute:cart-popup:toggle')        this.toggle(e.detail?.recalculate);
      if (e.type === 'enroute:cart-popup:open')          this.open(e.detail?.recalculate);
      if (e.type === 'enroute:cart-popup:close')         this.close();
      if (e.type === 'enroute:cart-popup:set-mode')      this.setMode(e.detail?.bagMode);
      if (e.type === 'enroute:cart-popup:open-wishlist') this.openWithWishlist();
    }

    _onDocClick(e) {
      // close an open wishlist switcher menu when clicking anywhere outside it
      const openSwitcher = this.querySelector('.wishlist-switcher.open');
      if (openSwitcher && !e.target.closest('.wishlist-switcher')) {
        this._setWishlistSwitcher(openSwitcher, false);
      }

      if (e.target.closest('[data-cart-popup-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.target.closest('[data-cart-popup-open]')) {
        e.preventDefault();
        this.open();
        return;
      }
      // update mode
      const modeEl = e.target.closest('[data-cart-popup-mode]');
      if (modeEl) {
        e.preventDefault();
        this.setMode(modeEl.dataset.cartPopupMode === 'bag');
      }
    }

    open(recalculate = true) {
      if (this._open && this._bagMode) return;
      clearTimeout(this._hideTimer);
      // A mid-flight tab transition must not swap the mode after the reset
      // below; drop it and un-pin the panels.
      this._tabs?.reset();
      this._open = true;
      this._bagMode = true;
      this._wishlistLoaded = false;  // reset tab-load flag; _wishlistEverFetched stays true
      this.hidden = false;
      // Render the closed state (wrapper at translateX(100%)) before activating,
      // otherwise the element jumps from display:none straight to .active and
      // the slide-in transition never runs — unlike close(), which animates.
      void this._cartEl?.offsetWidth;
      this._cartEl?.classList.add('active');
      if (this._cartEl) this._cartEl.dataset.mode = 'bag';
      this._updateTabClasses();
      this._hideModals();
      if (recalculate) this._recalculatePromoBar();
    }

    close() {
      // Let a mid-flight tab transition land immediately — otherwise its
      // tween would keep mutating styles on a closing drawer.
      this._tabs?.finish();
      this._tabs?.reset();
      if (!this._open) return;
      this._open = false;
      this._cartEl?.classList.remove('active');
      this._hideTimer = setTimeout(() => {
        if (!this._open) this.hidden = true;
      }, 500);
    }

    toggle(recalculate = true) {
      if (this._open) {
        this.close();
      } else {
        if (window.enrouteCart?.forceRefresh) window.enrouteCart?.requestCartUpdate?.();
        this.open(recalculate);
      }
    }

    setMode(bagMode) {
      const isBag = Boolean(bagMode);
      const target = isBag ? 0 : 1;

      this._prepareModeData(isBag);

      if (this._bagMode === isBag && !this._tabs?.transitioning) {
        // Already resting on that tab; re-assert state only.
        this._updateTabClasses(isBag);
        if (this._cartEl) this._cartEl.dataset.mode = isBag ? 'bag' : 'wishlist';
        return;
      }
      if (this._open && this._tabs?.animateTo(target)) {
        // The mode itself flips when the tween lands (the container commits); the tab
        // underline moves right away so the tap feels acknowledged.
        this._updateTabClasses(isBag);
        return;
      }
      // No panel groups, drawer closed or reduced motion: instant swap.
      this._bagMode = isBag;
      this._updateTabClasses(isBag);
      if (this._cartEl) this._cartEl.dataset.mode = isBag ? 'bag' : 'wishlist';
    }

    // Data side effects of entering a mode (wishlist fetch/decorate, promo-bar
    // recalc). Split from setMode so gestures/commits can start this work the
    // moment they are released, before the settle tween ends.
    _prepareModeData(isBag) {
      const provider = window.wishlistProvider;

      if (!isBag && !this._wishlistEverFetched && provider) {
        // Very first time wishlist tab is opened (across all cart-popup sessions):
        // fetch from server + decorate product availability.
        provider.init(
          true,
          true,
          () => { this._wishlistLoaded = true; this._wishlistEverFetched = true; provider.emitUpdated(); },
          () => provider.emitUpdated()
        );
      } else if (!isBag && this._wishlistEverFetched && !this._wishlistLoaded && provider) {
        // Cart was re-opened (open() reset _wishlistLoaded). Data already in memory;
        // only decorate to refresh availability, but suppress re-fetch.
        this._wishlistLoaded = true;
        provider.init(false, true, () => provider.emitUpdated(), () => provider.emitUpdated());
      } else if (isBag) {
        setTimeout(() => this._recalculatePromoBar(), 1000);
      }
    }

    openWithWishlist() {
      const needLogin = Boolean(window._erj_?.settings?.need_wishlist_login);
      const cid = typeof customerId !== 'undefined' ? customerId : null;
      if (!cid && needLogin) {
        window.dispatchEvent(new CustomEvent('enroute:open-login-modal', {
          detail: { source: 'wishlist-header' },
        }));
        return;
      }
      if (!this._open) this.open(true);
      this.setMode(false);
    }

    _updateTabClasses(isBag = this._bagMode) {
      this._cartEl?.querySelector('.cart-top-left')?.classList.toggle('selectedcard', isBag);
      this._cartEl?.querySelector('.cart-top-right')?.classList.toggle('selectedcard', !isBag);
      // Underline position: 0% under the bag tab, 100% (one half-width) under
      // wishlist. The ::after transform transitions between the two on commit.
      this._cartEl?.querySelector('.cart-top-left')?.style.setProperty('--tab-ink-x', isBag ? '0%' : '100%');
    }

    _hideModals() {
      document.getElementById('mobileconfirmationtoast')?.hide();
    }

    _recalculatePromoBar() {
      const cart = window.enrouteCart?.state;
      if (!cart) return;
      window.__enroutePromoBarState = { cart };
      this._cartEl?.querySelectorAll('promo-bar, progress-bar-by-count').forEach((el) => {
        // free-gift.js may register these elements after the cart first opens.
        // Their connectedCallback reads the cached state above once upgraded.
        if (typeof el.refresh === 'function') el.refresh(cart);
      });
    }

    // ---- Native wishlist tab ----

    get provider() {
      return window.wishlistProvider;
    }

    _onWishlist() {
      this._renderWishlist();
    }

    _onWishlistClick(event) {
      const trigger = event.target.closest('[data-wl-action]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();

      const p = this.provider;
      const d = trigger.dataset;

      switch (d.wlAction) {
        case 'toggle-menu': {
          const sw = trigger.closest('.wishlist-switcher');
          if (sw) this._setWishlistSwitcher(sw, !sw.classList.contains('open'));
          break;
        }
        case 'set-current':
          this._setWishlistSwitcher(trigger.closest('.wishlist-switcher'), false);
          if (p) p.setDefault(d.localId, () => {}, (err) => console.log(err));
          break;
        case 'add-wishlist':
          this._setWishlistSwitcher(trigger.closest('.wishlist-switcher'), false);
          if (p) p.addWishlist(() => {});
          break;
        case 'save-local':
          this._setWishlistSwitcher(trigger.closest('.wishlist-switcher'), false);
          if (p) p.saveLocalWishlist();
          break;
        case 'quantity': {
          const item = this._findCurrentItem(d.id);
          const next = Number(d.quantity);
          if (!item || !item.exists) break;
          if (!item.available) {
            // Sold-out lines can't change quantity; with the remove button
            // gone, their "-" removes the line outright.
            if (next < item.quantity) {
              p.removeWishlistItem(item, () => {}, (err) => console.log(err));
              this._renderWishlist();
            }
            break;
          }
          if (next < 1) {
            // No separate remove button: stepping 1 down to 0 removes the line.
            p.removeWishlistItem(item, () => {}, (err) => console.log(err));
            this._renderWishlist();
            break;
          }
          item.quantity = next;
          p.newQuantity(item);
          this._renderWishlist();
          break;
        }
        case 'add':
          window.enrouteCart?.addToCart?.({
            id: d.variantId,
            quantity: Number(d.quantity) || 1,
            productId: d.productId,
            handle: d.handle,
          });
          break;
        case 'notify':
          this._openBackInStockModal(d);
          break;
        case 'add-all':
          if (p) window.enrouteCart?.addAllFromWishlistToCart?.(p.currentWishlist);
          break;
        case 'checkout-all':
          if (p) this._quickCheckout(p.currentWishlist, trigger);
          break;
        default:
          break;
      }
    }

    // Wishlist switcher popover open/close state lives on the .wishlist-switcher
    // wrapper (CSS drives menu + chevron off `.open`).
    _setWishlistSwitcher(switcher, open) {
      if (!switcher) return;
      switcher.classList.toggle('open', open);
      switcher.querySelector('[data-wl-action="toggle-menu"]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    _findCurrentItem(id) {
      const p = this.provider;
      const items = p && p.currentWishlist && Array.isArray(p.currentWishlist.wishlistItems)
        ? p.currentWishlist.wishlistItems
        : [];
      return items.find((it) => it.id === id) || null;
    }

    // Sold-out wishlist lines open the shared back-in-stock modal instead of
    // adding to the bag. The wishlist item already carries everything the
    // modal needs (variant id, product id/handle, image), so no extra fetch.
    _openBackInStockModal(d) {
      if (typeof BackInStockModal === 'undefined') return;
      const variant = { id: d.variantId, image: d.image || '' };
      const bis = new BackInStockModal(variant, this, {
        productId: d.productId,
        handle: d.handle,
        title: d.title || '',
        firstMediaImage: d.image || '',
      });
      // The modal overlay ships with z-index 102, far below the cart drawer's
      // 999; lift it so the modal shows above the open drawer.
      if (bis.modal) bis.modal.style.zIndex = '100000';
    }

    _renderWishlist() {
      const p = this.provider;
      const countNode = this.querySelector('[data-cart-wishlist-count]');
      const infoNode = this.querySelector('[data-cart-wishlist-info]');
      const bodyNode = this.querySelector('[data-cart-wishlist-body]');
      if (!p) return;

      const current = p.currentWishlist || { wishlistItems: [] };
      const items = Array.isArray(current.wishlistItems) ? current.wishlistItems : [];

      if (countNode) countNode.textContent = String(items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0));
      const newInfo = this._wishlistInfoHtml();
      const newBody = this._wishlistBodyHtml();
      if (infoNode && infoNode.innerHTML !== newInfo) infoNode.innerHTML = newInfo;
      if (bodyNode && bodyNode.innerHTML !== newBody) bodyNode.innerHTML = newBody;
    }

    _wishlistInfoHtml() {
      const p = this.provider;
      const t = this._wishlistCfg.i18n || {};
      const local = p.localWishlist || {};
      const current = p.currentWishlist || {};
      const userLists = p.userWishlists && Array.isArray(p.userWishlists.wishlists) ? p.userWishlists.wishlists : [];
      const hasError = Boolean(p.storeObject && p.storeObject.error);
      const localIsCurrent = local.localId === current.localId;

      if (userLists.length && !hasError) {
        // Menu lists every OTHER wishlist (the active one is the trigger's
        // label), the unsaved local list when it has items, and create-new.
        let menu = '';
        userLists
          .filter((item) => item.localId !== current.localId)
          .forEach((item) => {
            menu += `<li><a class="wishlist-switcher-option" href="#" data-wl-action="set-current" data-local-id="${esc(
              item.localId
            )}">${esc(item.name)}</a></li>`;
          });
        if (!localIsCurrent && local.wishlistItems && local.wishlistItems.length) {
          menu += `<li><a class="wishlist-switcher-option" href="#" data-wl-action="set-current" data-local-id="${esc(
            local.localId
          )}">Local wishlist</a></li>`;
        }
        menu += `<li><a class="wishlist-switcher-option wishlist-switcher-create" href="#" data-wl-action="add-wishlist">${esc(
          t.plus_new
        )}</a></li>`;

        return (
          '<div class="wishlist-switcher">' +
          '<div class="wishlist-switcher-trigger">' +
          '<div class="wishlist-switcher-toggle" data-wl-action="toggle-menu" role="button" tabindex="0" aria-expanded="false">' +
          `<span class="wishlist-switcher-name">${localIsCurrent ? esc(t.unsaved) : esc(current.name)}</span>` +
          '<span class="wishlist-switcher-chevron" aria-hidden="true"><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5"/></svg></span>' +
          '</div>' +
          (localIsCurrent
            ? `<a class="wishlist-switcher-save" data-wl-action="save-local">${esc(t.save_on_account)}</a>`
            : '') +
          '</div>' +
          `<ul class="wishlist-switcher-menu">${menu}</ul>` +
          '</div>'
        );
      }

      // No saved (account) wishlists yet (or the list request failed).
      let html = '<div class="wishlist-info-line"><div class="wishlist-info-action">';
      if (p.isLoggedIn()) {
        if (!hasError) html += `<a data-wl-action="save-local" class="cart-plus-add-btn">${esc(t.save)}</a>`;
      } else {
        html += `<a href="${esc(window._erj_.loginUrl())}" class="cart-plus-add-btn">${esc(t.log_in)}</a>`;
      }
      html += '</div></div>';
      return html;
    }

    _wishlistBodyHtml() {
      const p = this.provider;
      const t = this._wishlistCfg.i18n || {};
      const syncing = p.syncingLocalData || p.syncingUserData;
      const current = p.currentWishlist || { wishlistItems: [] };
      const items = Array.isArray(current.wishlistItems) ? current.wishlistItems : [];

      if (syncing) {
        return '<div class="cart-wishlist-loader cart-wishlist-section"><span class="loader-dot-hvg-white"><i></i><i></i><i></i></span></div>';
      }

      let html = '<div class="hvg-scrollable-container cart-wishlist-section">';
      if (items.length) {
        html += '<ul class="hvg-top-list">';
        items.filter((it) => it.exists).forEach((it) => { html += this._wishlistItemHtml(it); });
        html += '</ul>';
      } else {
        const logo = (this._wishlistCfg.assets && this._wishlistCfg.assets.emptyLogo) || '';
        html +=
          '<div class="empty hvg-empty">' +
          `<div class="cartlogo" style="filter: grayscale(100);opacity: 0.5;"><img src="${esc(
            logo
          )}" alt="Wishlist is empty" width="100" height="100"/></div>` +
          `<div>${esc(t.empty)}</div>` +
          '</div>';
      }
      html += '</div>';

      const purchasable = items.filter((item) => item.exists && item.available);
      const soldOutQty = items
        .filter((item) => item.exists && !item.available)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (purchasable.length > 0) {
        // Same purchasable set _quickCheckout posts, so the label matches what
        // would actually be charged.
        const count = purchasable.reduce((sum, item) => sum + item.quantity, 0);
        const totalCents = purchasable.reduce((sum, item) => sum + (item.priceVal || 0) * item.quantity, 0);
        // formatMoney may wrap the amount in <span class=money>…</span>; strip
        // the HTML so esc() doesn't render the tags as literal text.
        const totalLabel = (
          typeof Shopify !== 'undefined' && Shopify.formatMoney ? Shopify.formatMoney(totalCents) : String(totalCents)
        ).replace(/<[^>]*>/g, '');
        const countLabel = `(${count} ${count === 1 ? t.item : t.items})`;
        const soldOutNote =
          soldOutQty > 0 && t.sold_out_note
            ? `<div class="hvg-wishlist-soldout-note">${esc(String(t.sold_out_note).replace('{{count}}', String(soldOutQty)))}</div>`
            : '';
        html +=
          '<div class="bottom cart-wishlist-section"><div class="totals">' +
          '<div class="grid">' +
          soldOutNote +
          `<a class="hvg-wishlist-footer-btn hvg-wishlist-footer-btn--checkout" data-wl-action="checkout-all">${esc(t.checkout)} · ${esc(totalLabel)} ${esc(countLabel)}</a>` +
          '</div>' +
          '<div class="grid">' +
          `<a class="hvg-wishlist-footer-btn hvg-wishlist-footer-btn--outline" data-wl-action="add-all">${esc(t.add_all)}</a>` +
          '</div>' +
          '</div></div>';
      }
      return html;
    }

    // Build a checkout from the active wishlist only. Posting to cart/add.js
    // would merge these quantities with the customer's existing bag.
    async _quickCheckout(wishlist, trigger) {
      if (this._checkoutPending) return;
      const items = (wishlist?.wishlistItems || [])
        .filter((item) => item.exists && item.available)
        .map((item) => ({ id: item.variantId, quantity: item.quantity }));
      if (!items.length) return;

      // Never silently check out only part of the wishlist.
      if (items.length > 70) {
        window.enrouteCart?.showCartError?.('Quick checkout supports up to 70 wishlist items. Please reduce your wishlist and try again.');
        return;
      }

      this._checkoutPending = true;
      if (trigger) trigger.dataset.loading = 'true';
      try {
        const expected = new Map();
        for (const item of items) {
          const id = `gid://shopify/ProductVariant/${item.id}`;
          if (!/^\d+$/.test(String(item.id)) || !Number.isInteger(item.quantity) || item.quantity < 1) {
            throw new Error('Please refresh your wishlist and try again.');
          }
          expected.set(id, (expected.get(id) || 0) + item.quantity);
        }
        const lines = Array.from(expected, ([merchandiseId, quantity]) => ({ merchandiseId, quantity }));
        const country = window.Shopify?.country || null;
        const input = { lines };
        if (country) input.buyerIdentity = { countryCode: country };
        if (this._wishlistCfg.discountCode) input.discountCodes = [this._wishlistCfg.discountCode];
        // Tokenless Cart API access is supported. Omit cookies so this request
        // cannot replace the Online Store cart cookie. Never persist this cart
        // into enrouteCart, localStorage or the Ajax cart session.
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: `mutation WishlistCheckout($input: CartInput!, $country: CountryCode) @inContext(country: $country) {
              cartCreate(input: $input) {
                cart {
                  checkoutUrl
                  lines(first: 70) {
                    nodes { quantity merchandise { ... on ProductVariant { id } } }
                    pageInfo { hasNextPage }
                  }
                }
                userErrors { message }
                warnings { message }
              }
            }`,
            variables: { input, country },
          }),
        });
        const result = await response.json();
        if (!response.ok || result.errors?.length) {
          throw new Error(result.errors?.[0]?.message || 'Unable to start wishlist checkout. Please try again.');
        }
        const payload = result.data?.cartCreate;
        const problem = payload?.userErrors?.[0] || payload?.warnings?.[0];
        if (problem) throw new Error(problem.message);
        const cart = payload?.cart;
        const actual = new Map();
        for (const line of cart?.lines?.nodes || []) {
          const id = line.merchandise.id;
          actual.set(id, (actual.get(id) || 0) + line.quantity);
        }
        if (!cart?.checkoutUrl || cart.lines?.pageInfo?.hasNextPage || actual.size !== expected.size ||
            Array.from(expected).some(([id, quantity]) => actual.get(id) !== quantity)) {
          throw new Error('Some wishlist items could not be checked out in the requested quantities. Please refresh your wishlist and try again.');
        }
        window.location.href = cart.checkoutUrl;
      } catch (err) {
        window.enrouteCart?.showCartError?.(err && err.message);
      } finally {
        this._checkoutPending = false;
        if (trigger) delete trigger.dataset.loading;
      }
    }

    _wishlistItemHtml(item) {
      const t = this._wishlistCfg.i18n || {};
      const options = Array.isArray(item.options)
        ? item.options.filter((o) => o !== 'Default Title').map((o) => esc(o)).join(', ')
        : '';
      const showCompare = item.compareAtPriceVal != null && item.compareAtPriceVal > item.priceVal;
      const thumb = item.featured_image || '';

      return (
        `<li class="hvg-list-cart-item"><div class="grid">` +
        `<a href="${esc(item.url)}" style="background-image: url(${esc(thumb)})" class="thumb"></a>` +
        '<div class="info"><div class="grid"><div class="cart-item-info">' +
        `<div class="cart-item-title"><a href="${esc(item.url)}" class="title">${esc(item.title)}</a><br />` +
        (options ? `<span style="color:#777474;">${options}</span>` : '') +
        '</div>' +
        '<div class="cart-item-prices">' +
        `<div class="price${showCompare ? ' cartRedPrice' : ''}">${item.price || ''}</div>` +
        (showCompare ? `<div class="price-out"><s>${item.compareAtPrice || ''}</s></div>` : '') +
        '</div></div></div>' +
        '<div class="grid"><div class="cart-switcher-row"><div class="cart-item-manipulator">' +
        '<div class="cart-switcher-row-q">' +
        `<div class="cart-quantity-manipulator" data-wl-action="quantity" data-id="${esc(item.id)}" data-quantity="${item.quantity - 1}">-</div>` +
        `<div class="val">${item.quantity}</div>` +
        `<div class="cart-quantity-manipulator" data-wl-action="quantity" data-id="${esc(item.id)}" data-quantity="${item.quantity + 1}">+</div>` +
        '</div></div>' +
        (item.available
          ? `<div><a class="cart-plus-add-btn" data-wl-action="add" data-variant-id="${esc(item.variantId)}" data-quantity="${item.quantity}" data-product-id="${esc(item.productId)}" data-handle="${esc(item.handle)}">${esc(t.add_to_bag)}</a></div>`
          : `<div><a class="cart-plus-add-btn" data-wl-action="notify" data-variant-id="${esc(item.variantId)}" data-product-id="${esc(item.productId)}" data-handle="${esc(item.handle)}" data-title="${esc(item.title)}" data-image="${esc(item.featured_image || '')}">${esc(t.sold_out_notify || 'Sold out - Notify me')}</a></div>`) +
        '</div></div></div></li>'
      );
    }
  }

  CartPopup._WINDOW_EVENTS = [
    'enroute:cart-popup:toggle',
    'enroute:cart-popup:open',
    'enroute:cart-popup:close',
    'enroute:cart-popup:set-mode',
    'enroute:cart-popup:open-wishlist',
  ];

  customElements.define('cart-popup', CartPopup);
})();
